import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip,
  LinearProgress, Tooltip, Grid, Collapse, Card, CardContent, Divider,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, CheckCircle as PaidIcon,
  Add as AddIcon, Refresh as RefreshIcon,
  MonetizationOn as SalaryIcon, RestartAlt as ResetIcon,
  AccountBalance as BankFileIcon,
  Upload as UploadIcon, Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon, People as PeopleIcon,
  EmojiEvents as AwardIcon, AccessTime as CompOffIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer, PieChart, Pie,
  Cell, LabelList
} from 'recharts';
import api from '../services/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const _today = new Date();

const fmt2 = (n) => parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Salary Sheet ────────────────────────────────────────────────────────────

const PAYMENT_MODES = ['Bank Transfer', 'Cash', 'Cheque', 'UPI'];
const TODAY_STR = new Date().toISOString().split('T')[0];

function PayConfirmDialog({ row, onClose, onConfirm }) {
  const [mode, setMode]   = useState(row._paymentMode || 'Bank Transfer');
  const [date, setDate]   = useState(row._paymentDate || TODAY_STR);
  const [ref, setRef]     = useState(row._transactionRef || '');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    await onConfirm(mode, date, ref);
    setSaving(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark as Paid — {row.employeeName}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <FormControl size="small" fullWidth>
              <InputLabel>Payment Mode</InputLabel>
              <Select value={mode} label="Payment Mode" onChange={e => setMode(e.target.value)}>
                {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Payment Date" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }} value={date} onChange={e => setDate(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Transaction Ref / Cheque No" size="small" fullWidth
              value={ref} onChange={e => setRef(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">
              Net Payable: <strong>₹{parseFloat(row.netPayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={handleConfirm} disabled={saving}>
          {saving ? 'Processing…' : 'Confirm Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SalarySheet() {
  const [month, setMonth] = useState(MONTHS[_today.getMonth()]);
  const [year, setYear] = useState(_today.getFullYear().toString());
  const [cutoffDay, setCutoffDay] = useState('');  // blank = use last day of month
  const [workingDays, setWorkingDays] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [saveResult, setSaveResult] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMonth, setImportMonth] = useState(MONTHS[_today.getMonth()]);
  const [importYear, setImportYear] = useState(_today.getFullYear().toString());
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason]   = useState('');
  const [payTarget, setPayTarget] = useState(null);
  const [bankFileOpen, setBankFileOpen] = useState(false);
  const [debitAccount, setDebitAccount] = useState('922020045269368');
  const [bankFileLoading, setBankFileLoading] = useState(false);
  const [bankFileIncomplete, setBankFileIncomplete] = useState([]);
  // Recovery state (Phase 2)
  const [recoveryPending, setRecoveryPending] = useState([]);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoverySelections, setRecoverySelections] = useState({});
  const [applyingRecovery, setApplyingRecovery] = useState(false);
  // Bulk apply payment date + remark
  const [bulkApplyOpen, setBulkApplyOpen] = useState(false);
  const [bulkDate, setBulkDate] = useState(TODAY_STR);
  const [bulkRemark, setBulkRemark] = useState('');

  const yearOptions = [];
  for (let y = _today.getFullYear(); y >= _today.getFullYear() - 3; y--) yearOptions.push(y.toString());

  const loadSheet = useCallback(async () => {
    setLoading(true);
    setError('');
    setSaveResult('');
    setRecoveryPending([]);
    try {
      const params = {};
      if (cutoffDay && parseInt(cutoffDay) > 0) params.cutoffDay = cutoffDay;
      const res = await api.get(`/salary/sheet/${month}-${year}`, { params });
      const { sheet, workingDays: wd, recoveryPending: rp } = res.data.data;
      setWorkingDays(wd);
      setRows(sheet.map(r => ({ ...r, _paymentMode: r.paymentMode || 'Bank Transfer', _paymentDate: r.paymentDate || TODAY_STR, _transactionRef: r.transactionRef || '', _remarks: r.remarks || '' })));
      if (rp && rp.length > 0) {
        setRecoveryPending(rp);
        // Pre-select all for recovery
        const sel = {};
        rp.forEach(r => { sel[r.projectionId] = true; });
        setRecoverySelections(sel);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load salary sheet');
    } finally {
      setLoading(false);
    }
  }, [month, year, cutoffDay]);

  // Auto-load on mount and whenever month/year changes
  useEffect(() => { loadSheet(); }, [loadSheet]);

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      // Recalculate earnedSalary when extraDays changes (for non-override employees)
      const OVERRIDE_CODES = new Set(['JBD00039', 'JBD00050', 'JBD00051', 'JBD00066']);
      if (field === 'extraDays' && !OVERRIDE_CODES.has(r.employeeCode)) {
        const extra    = parseFloat(value) || 0;
        const present  = parseFloat(r.actualPresentDays || r.presentDays || 0);
        const wdays    = parseFloat(r.workingDays || 1);
        const basic    = parseFloat(r.basicSalary || 0);
        updated.earnedSalary = parseFloat(((present + extra) * basic / wdays).toFixed(2));
        updated.netPayable   = parseFloat((updated.earnedSalary + (parseFloat(r.awardAmount) || 0) + (parseFloat(r.allowance) || 0) + (parseFloat(r.arrears) || 0) + (parseFloat(r.compOffPay) || 0) - (parseFloat(r.advanceDeduction) || 0) - (parseFloat(r.prepaidDeduction) || 0) - (parseFloat(r.otherDeductions) || 0)).toFixed(2));
      }
      // When any component of net payable changes, recalculate netPayable automatically
      const netAffectingFields = ['earnedSalary', 'awardAmount', 'allowance', 'arrears', 'compOffPay', 'advanceDeduction', 'prepaidDeduction', 'otherDeductions'];
      if (netAffectingFields.includes(field)) {
        const earned    = parseFloat(field === 'earnedSalary'     ? value : r.earnedSalary)     || 0;
        const award     = parseFloat(field === 'awardAmount'       ? value : r.awardAmount)       || 0;
        const allowance = parseFloat(field === 'allowance'         ? value : r.allowance)         || 0;
        const arrears   = parseFloat(field === 'arrears'           ? value : r.arrears)           || 0;
        const compOff   = parseFloat(field === 'compOffPay'        ? value : r.compOffPay)        || 0;
        const advDed    = parseFloat(field === 'advanceDeduction'  ? value : r.advanceDeduction)  || 0;
        const prepaid   = parseFloat(field === 'prepaidDeduction'  ? value : r.prepaidDeduction)  || 0;
        const otherDed  = parseFloat(field === 'otherDeductions'   ? value : r.otherDeductions)   || 0;
        updated.netPayable = parseFloat((earned + award + allowance + arrears + compOff - advDed - prepaid - otherDed).toFixed(2));
      }
      return updated;
    }));
  };

  const handleApplyRecovery = async () => {
    const selected = recoveryPending.filter(r => recoverySelections[r.projectionId]);
    if (!selected.length) return;
    setApplyingRecovery(true);
    try {
      await api.post('/salary/recovery-apply', {
        currentMonthYear: `${month}-${year}`,
        recoveries: selected.map(r => ({
          projectionId: r.projectionId,
          employeeCode: r.employeeCode,
          employeeName: r.employeeName,
          recoveryAmount: r.recoveryAmount,
          prevMonthYear: r.monthYear,
        })),
      });
      setRecoveryOpen(false);
      setRecoveryPending([]);
      loadSheet();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to apply recovery');
    } finally {
      setApplyingRecovery(false);
    }
  };

  const handleWaiveRecovery = async (projectionId) => {
    try {
      await api.post(`/salary/recovery-waive/${projectionId}`);
      setRecoveryPending(prev => prev.filter(r => r.projectionId !== projectionId));
    } catch (e) {
      alert('Failed to waive recovery');
    }
  };

  const handleDownloadTemplate = () => {
    const my = `${importMonth}-${importYear}`;
    window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1'}/salary/template/${my}`, '_blank');
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    // Read first few rows for preview using FileReader + xlsx (loaded via backend)
    // Just show file name + size for now; full preview comes from backend
    setImportPreview({ name: file.name, size: (file.size / 1024).toFixed(1) + ' KB' });
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/salary/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult({ success: true, ...res.data });
      setImportFile(null);
      setImportPreview(null);
    } catch (e) {
      setImportResult({ success: false, message: e.response?.data?.message || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  // Save all rows (unsaved + saved-but-edited)
  const handleSaveAll = async () => {
    setSaving(true);
    setSaveResult('');
    let ok = 0, fail = 0;
    for (const row of rows) {
      if (row.status === 'Paid' || row.status === 'Approved' || row.status === 'Rejected') continue;
      try {
        await api.post('/salary/sheet', {
          monthYear: `${month}-${year}`,
          employeeCode: row.employeeCode,
          employeeName: row.employeeName,
          basicSalary: row.basicSalary,
          workingDays: row.workingDays,
          presentDays: row.presentDays,
          extraDays: row.extraDays || 0,
          earnedSalary: row.earnedSalary,
          awardAmount: row.awardAmount,
          allowance: row.allowance || 0,
          arrears: row.arrears || 0,
          compOffPay: row.compOffPay || 0,
          advanceDeduction: row.advanceDeduction,
          otherDeductions: row.otherDeductions,
          netPayable: row.netPayable,
          paymentMode: row._paymentMode,
          paymentDate: row._paymentDate || null,
          transactionRef: row._transactionRef,
          status: row.status || 'Draft',
          remarks: row._remarks,
          // Projection fields
          projectedDays: row.projectedDays || 0,
          projectedDaysList: row.projectedDaysList || [],
          leaveDaysInRange: row.leaveDaysInRange || 0,
          projectedAmount: row.projectedAmount || 0,
          cutoffDay: row.cutoffDay || null,
          dailyRate: row.workingDays > 0 ? Math.round((row.basicSalary / row.workingDays) * 100) / 100 : 0,
        });
        ok++;
      } catch { fail++; }
    }
    setSaveResult(`Saved ${ok} rows${fail ? `, ${fail} failed` : ''}.`);
    setSaving(false);
    loadSheet();
  };

  const handleMarkAllPaid = async () => {
    const approvedRows = rows.filter(r => r.id && r.status === 'Approved');
    if (!approvedRows.length) return alert('No Approved rows to mark as paid. Approve rows first.');
    if (!window.confirm(`Mark ${approvedRows.length} Approved employees as Paid?`)) return;
    setMarkingPaid(true);
    let ok = 0, fail = 0;
    for (const row of approvedRows) {
      try {
        await api.put(`/salary/sheet/${row.id}/mark-paid`, {
          paymentMode: row._paymentMode,
          paymentDate: row._paymentDate || TODAY_STR,
          transactionRef: row._transactionRef
        });
        ok++;
      } catch { fail++; }
    }
    setSaveResult(`Marked ${ok} as Paid${fail ? `, ${fail} failed` : ''}.`);
    setMarkingPaid(false);
    loadSheet();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this salary record?')) return;
    try { await api.delete(`/salary/sheet/${id}`); loadSheet(); }
    catch (e) { alert(e.response?.data?.message || 'Delete failed'); }
  };

  const handleReset = async (id) => {
    if (!window.confirm('Reset this row? All saved data (status, payment info, deductions) will be cleared and recalculated fresh.')) return;
    try { await api.delete(`/salary/sheet/${id}?force=true`); loadSheet(); }
    catch (e) { alert(e.response?.data?.message || 'Reset failed'); }
  };

  // Status transition helpers
  const changeStatus = async (row, newStatus, extra = {}) => {
    try {
      await api.put(`/salary/sheet/${row.id}/status`, { status: newStatus, ...extra });
      loadSheet();
    } catch (e) {
      alert(e.response?.data?.message || 'Status update failed');
    }
  };

  const handleStatusSelect = (row, newStatus) => {
    if (newStatus === 'Rejected') {
      setRejectTarget(row);
      setRejectReason('');
    } else if (newStatus === 'Paid') {
      setPayTarget(row);
    } else {
      changeStatus(row, newStatus);
    }
  };

  const confirmReject = async () => {
    await changeStatus(rejectTarget, 'Rejected', { rejectionReason: rejectReason });
    setRejectTarget(null);
    setRejectReason('');
  };

  const confirmPay = async (paymentMode, paymentDate, transactionRef) => {
    try {
      await api.put(`/salary/sheet/${payTarget.id}/mark-paid`, { paymentMode, paymentDate, transactionRef });
      loadSheet();
    } catch (e) {
      alert(e.response?.data?.message || 'Mark paid failed');
    } finally {
      setPayTarget(null);
    }
  };

  // Bulk: approve all saved Draft rows
  const handleApproveAll = async () => {
    const draftRows = rows.filter(r => r.id && r.status === 'Draft');
    if (!draftRows.length) return alert('No saved Draft rows to approve.');
    if (!window.confirm(`Approve ${draftRows.length} employees?`)) return;
    setMarkingPaid(true);
    let ok = 0, fail = 0;
    for (const row of draftRows) {
      try { await api.put(`/salary/sheet/${row.id}/status`, { status: 'Approved' }); ok++; }
      catch { fail++; }
    }
    setSaveResult(`Approved ${ok} rows${fail ? `, ${fail} failed` : ''}.`);
    setMarkingPaid(false);
    loadSheet();
  };

  // Bank file: generate AXIS + All-Banks xlsx via backend
  const handleBankFile = () => setBankFileOpen(true);

  const handleBulkApply = async () => {
    // Apply payment date and/or remark to all rows regardless of status
    const newDate = bulkDate || null;
    const newRemark = bulkRemark !== '' ? bulkRemark : null;
    setRows(prev => prev.map(r => ({
      ...r,
      _paymentDate: newDate || r._paymentDate,
      _remarks: newRemark !== null ? newRemark : r._remarks,
    })));
    // Persist the new date immediately for already-Paid rows
    if (newDate) {
      const paidRows = rows.filter(r => r.status === 'Paid' && r.id);
      for (const r of paidRows) {
        try { await api.patch(`/salary/sheet/${r.id}/payment-date`, { paymentDate: newDate }); }
        catch (_) { /* best-effort */ }
      }
    }
    setBulkApplyOpen(false);
  };

  const handleBankFileDownload = async () => {
    setBankFileLoading(true);
    setBankFileIncomplete([]);
    try {
      const monthYear = `${month}-${year}`;
      const res = await api.get(`/salary/bank-files/${monthYear}`, {
        params: { debitAccount: debitAccount.trim() }
      });
      const { axisBase64, axisFilename, allBanksBase64, allBanksFilename, axisSummary, allBanksSummary } = res.data;

      const download = (b64, filename) => {
        const byteChars = atob(b64);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      };

      if (axisSummary.count > 0)     download(axisBase64,    axisFilename);
      if (allBanksSummary.count > 0) download(allBanksBase64, allBanksFilename);

      if (axisSummary.count === 0 && allBanksSummary.count === 0) {
        alert('No employees with bank details found in Approved rows.');
      }

      setBankFileOpen(false);
    } catch (e) {
      const data = e.response?.data;
      if (e.response?.status === 422 && data?.incomplete?.length > 0) {
        setBankFileIncomplete(data.incomplete);
      } else {
        alert(data?.message || 'Failed to generate bank files');
      }
    } finally {
      setBankFileLoading(false);
    }
  };

  const openEdit = (row) => { setEditRow({ ...row }); setEditOpen(true); };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      await api.post('/salary/sheet', {
        monthYear: `${month}-${year}`,
        employeeCode: editRow.employeeCode,
        employeeName: editRow.employeeName,
        basicSalary: editRow.basicSalary,
        workingDays: editRow.workingDays,
        presentDays: editRow.presentDays,
        earnedSalary: editRow.earnedSalary,
        awardAmount: editRow.awardAmount,
        allowance: editRow.allowance || 0,
        arrears: editRow.arrears || 0,
        compOffPay: editRow.compOffPay || 0,
        advanceDeduction: editRow.advanceDeduction,
        otherDeductions: editRow.otherDeductions,
        netPayable: editRow.netPayable,
        paymentMode: editRow._paymentMode,
        paymentDate: editRow._paymentDate || null,
        transactionRef: editRow._transactionRef,
        status: editRow.status || 'Draft',
        remarks: editRow._remarks
      });
      setEditOpen(false);
      loadSheet();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    finally { setEditSaving(false); }
  };

  const paidCount     = rows.filter(r => r.status === 'Paid').length;
  const approvedCount = rows.filter(r => r.status === 'Approved').length;
  const draftCount    = rows.filter(r => !r.status || r.status === 'Draft').length;
  const rejectedCount = rows.filter(r => r.status === 'Rejected').length;
  const totalNet = rows.reduce((s, r) => s + parseFloat(r.netPayable || 0), 0);
  const draftSavedCount    = rows.filter(r => r.id && r.status === 'Draft').length;
  const approvedSavedCount = rows.filter(r => r.id && r.status === 'Approved').length;

  return (
    <Box>
      {/* Filter + Action Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
              {MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
              {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title="Leave blank to use full month. Set to e.g. 29 if processing salary before month end — remaining working days will be projected as Present." arrow>
            <TextField
              size="small" label="Cutoff Day" type="number"
              value={cutoffDay}
              onChange={e => setCutoffDay(e.target.value)}
              placeholder="Last day"
              inputProps={{ min: 1, max: 31 }}
              sx={{ width: 110 }}
            />
          </Tooltip>
          <Button variant="contained" onClick={loadSheet} startIcon={<RefreshIcon />}>
            Load Sheet
          </Button>

          {rows.length > 0 && (
            <>
              <Button variant="outlined" color="primary" onClick={handleSaveAll} disabled={saving || markingPaid}
                sx={{ ml: 1 }}>
                {saving ? 'Saving…' : 'Save All'}
              </Button>
              <Button variant="outlined" color="info" startIcon={<DownloadIcon />}
                onClick={() => { setBulkDate(TODAY_STR); setBulkRemark(''); setBulkApplyOpen(true); }}>
                Set Payment Date
              </Button>
              <Button variant="contained" color="info" onClick={handleApproveAll}
                disabled={saving || markingPaid || !draftSavedCount}
                startIcon={<PaidIcon />}>
                {markingPaid ? 'Processing…' : `Approve All (${draftSavedCount})`}
              </Button>
              <Button variant="contained" color="success" onClick={handleMarkAllPaid}
                disabled={saving || markingPaid || !approvedSavedCount}
                startIcon={<PaidIcon />}>
                {markingPaid ? 'Processing…' : `Mark All Paid (${approvedSavedCount})`}
              </Button>
              <Button variant="outlined" color="warning" startIcon={<ResetIcon />}
                disabled={saving || markingPaid || !rows.some(r => r.id)}
                onClick={async () => {
                  if (!window.confirm('Reset ALL saved rows to Draft? Salary figures will be recalculated fresh.')) return;
                  for (const r of rows.filter(row => row.id)) {
                    try { await api.delete(`/salary/sheet/${r.id}`); } catch {}
                  }
                  loadSheet();
                }}>
                Reset Draft
              </Button>
              <Button variant="outlined" color="secondary" startIcon={<BankFileIcon />}
                disabled={!approvedCount}
                onClick={handleBankFile}>
                Bank File ({approvedCount})
              </Button>
              <Button variant="outlined" color="info" startIcon={<UploadIcon />}
                onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); setImportPreview(null); }}>
                Import History
              </Button>
            </>
          )}

          {rows.length > 0 && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip label={`WD: ${workingDays}`} color="info" size="small" />
              {draftCount > 0     && <Chip label={`Draft: ${draftCount}`} color="warning" size="small" />}
              {approvedCount > 0  && <Chip label={`Approved: ${approvedCount}`} color="primary" size="small" />}
              {paidCount > 0      && <Chip label={`Paid: ${paidCount}`} color="success" size="small" />}
              {rejectedCount > 0  && <Chip label={`Rejected: ${rejectedCount}`} color="error" size="small" />}
              <Chip label={`Total: ₹${fmt2(totalNet)}`} variant="outlined" size="small" />
            </Box>
          )}
        </Box>
        {saveResult && <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setSaveResult('')}>{saveResult}</Alert>}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {/* Cutoff projection info banner */}
      {rows.some(r => (r.projectedDays || 0) > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={<Button size="small" color="inherit" onClick={loadSheet}>Refresh</Button>}>
          <strong>Projected Salary:</strong> Cutoff day {rows.find(r => r.cutoffDay)?.cutoffDay} — remaining working days assumed Present (leave-adjusted). Verify actual attendance after month end and use <em>Recovery</em> next month if needed.
        </Alert>
      )}

      {/* Recovery pending banner */}
      {recoveryPending.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button size="small" color="inherit" variant="outlined" onClick={() => setRecoveryOpen(true)}>Review Recoveries</Button>}>
          <strong>Recovery Pending:</strong> {recoveryPending.length} employee(s) had projected days last month that don't match actual attendance. Review and apply deductions to this month's salary.
        </Alert>
      )}

      {rows.length > 0 && !loading && (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.paper' } }}>
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>Employee</TableCell>
                <TableCell align="right">Basic (₹)</TableCell>
                <TableCell align="right">Present</TableCell>
                <TableCell align="right" sx={{ color: 'info.main' }}>
                  <Tooltip title="Extra days to add to present days for salary calculation (e.g. cutoff adjustment)">
                    <span>Extra Days</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ color: '#9C27B0' }}>CO Days</TableCell>
                <TableCell align="right">Earned (₹)</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>Award (₹)</TableCell>
                <TableCell align="right" sx={{ color: 'success.dark' }}>Allowance (₹)</TableCell>
                <TableCell align="right" sx={{ color: 'success.dark' }}>Arrears (₹)</TableCell>
                <TableCell align="right" sx={{ color: 'info.main' }}>Comp-Off (₹)</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>Adv.Ded (₹)</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>Prepaid Ded (₹)</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>Other Ded (₹)</TableCell>
                <TableCell align="right">Net Payable (₹)</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const isPaid     = row.status === 'Paid';
                const isApproved = row.status === 'Approved';
                const isRejected = row.status === 'Rejected';
                const isLocked   = isPaid || isApproved;
                const isManualOverride = ['JBD00039', 'JBD00050', 'JBD00051', 'JBD00066'].includes(row.employeeCode);
                const hasBankInfo = row.bankInfo?.bankAccountNumber && row.bankInfo?.bankIfscCode;
                const rowBg = isPaid     ? '#f0fdf4'
                            : isApproved ? '#e3f2fd'
                            : isRejected ? '#fef2f2'
                            : !row.isSaved ? '#fffbeb' : 'inherit';

                // Status options per current status
                const statusOptions = isPaid ? [] :
                  isApproved  ? ['Draft', 'Rejected', 'Paid'] :
                  isRejected  ? ['Draft'] :
                  /* Draft */   ['Approved', 'Rejected'];

                const statusColor = isPaid ? 'success' : isApproved ? 'primary' : isRejected ? 'error' : 'warning';

                return (
                  <TableRow key={row.id || `unsaved-${idx}`} sx={{ bgcolor: rowBg }}>
                    <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: rowBg, whiteSpace: 'nowrap', fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {row.employeeName}
                        {!hasBankInfo && row.isSaved && (
                          <Tooltip title="Missing bank account / IFSC — will be excluded from bank file">
                            <Box component="span" sx={{ color: 'warning.main', fontSize: 14, fontWeight: 700, cursor: 'help' }}>⚠</Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{fmt2(row.basicSalary)}</TableCell>
                    <TableCell align="right">
                      {(row.projectedDays || 0) > 0 ? (
                        <Tooltip arrow title={`Actual: ${row.actualPresentDays} + Projected: ${row.projectedDays} day(s) (Day ${(row.projectedDaysList||[]).join(', ')}). Leave in range: ${row.leaveDaysInRange||0} day(s).`}>
                          <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600, cursor: 'help' }}>
                            {row.presentDays} <Box component="span" sx={{ fontSize: '0.7rem' }}>({row.actualPresentDays}+{row.projectedDays}*)</Box>
                          </Box>
                        </Tooltip>
                      ) : row.presentDays}
                    </TableCell>
                    <TableCell align="right">
                      {isLocked
                        ? (parseFloat(row.extraDays || 0) > 0 ? row.extraDays : '—')
                        : <TextField size="small" type="number"
                            value={row.extraDays || 0}
                            inputProps={{ style: { width: 55, textAlign: 'right', padding: '2px 4px' }, step: 0.5, min: 0, max: 30 }}
                            variant="standard"
                            onChange={e => updateRow(idx, 'extraDays', e.target.value)}
                            onBlur={e => updateRow(idx, 'extraDays', parseFloat(e.target.value) || 0)} />
                      }
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#9C27B0', fontWeight: row.compOffDays > 0 ? 700 : 400 }}>
                      {row.compOffDays > 0 ? row.compOffDays : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {isManualOverride
                        ? <TextField size="small" type="number"
                            value={row.earnedSalary || 0}
                            inputProps={{ style: { width: 90, textAlign: 'right', padding: '2px 4px' }, step: 'any', min: 0 }}
                            variant="standard"
                            onChange={e => updateRow(idx, 'earnedSalary', e.target.value)}
                            onBlur={e => updateRow(idx, 'earnedSalary', parseFloat(e.target.value) || 0)} />
                        : fmt2(row.earnedSalary)
                      }
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {parseFloat(row.awardAmount || 0) > 0 ? fmt2(row.awardAmount) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.dark' }}>
                      {isLocked
                        ? (parseFloat(row.allowance || 0) > 0 ? fmt2(row.allowance) : '—')
                        : <TextField size="small" type="number" value={row.allowance || 0}
                            inputProps={{ style: { width: 90, textAlign: 'right', padding: '2px 4px' }, step: 'any', min: 0 }}
                            variant="standard"
                            onChange={e => updateRow(idx, 'allowance', e.target.value)}
                            onBlur={e => updateRow(idx, 'allowance', parseFloat(e.target.value) || 0)} />
                      }
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.dark' }}>
                      {isLocked
                        ? (parseFloat(row.arrears || 0) > 0 ? fmt2(row.arrears) : '—')
                        : <TextField size="small" type="number" value={row.arrears || 0}
                            inputProps={{ style: { width: 90, textAlign: 'right', padding: '2px 4px' }, step: 'any', min: 0 }}
                            variant="standard"
                            onChange={e => updateRow(idx, 'arrears', e.target.value)}
                            onBlur={e => updateRow(idx, 'arrears', parseFloat(e.target.value) || 0)} />
                      }
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'info.main' }}>
                      {parseFloat(row.compOffPay || 0) > 0
                        ? <Box component="span" title={`${row.compOffDays || 0} day(s) × daily rate`}>{fmt2(row.compOffPay)}</Box>
                        : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {parseFloat(row.advanceDeduction || 0) >= 1 ? fmt2(row.advanceDeduction) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {parseFloat(row.prepaidDeduction || 0) > 0 ? fmt2(row.prepaidDeduction) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {isLocked
                        ? (parseFloat(row.otherDeductions || 0) > 0 ? fmt2(row.otherDeductions) : '—')
                        : <TextField size="small" type="number" value={row.otherDeductions || 0}
                            inputProps={{ style: { width: 90, textAlign: 'right', padding: '2px 4px' }, step: 'any', min: 0 }}
                            variant="standard"
                            onChange={e => updateRow(idx, 'otherDeductions', e.target.value)}
                            onBlur={e => updateRow(idx, 'otherDeductions', parseFloat(e.target.value) || 0)} />
                      }
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {isManualOverride
                        ? <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
                            <TextField size="small" type="number" value={row.netPayable || 0}
                              inputProps={{ style: { width: 90, textAlign: 'right', padding: '2px 4px' }, step: 'any', min: 0 }}
                              variant="standard"
                              onChange={e => updateRow(idx, 'netPayable', e.target.value)}
                              onBlur={e => updateRow(idx, 'netPayable', parseFloat(e.target.value) || 0)} />
                            <Tooltip title="Save override values">
                              <span>
                                <IconButton size="small" color="primary"
                                  onClick={async () => {
                                    try {
                                      await api.post('/salary/sheet', {
                                        monthYear: `${month}-${year}`,
                                        employeeCode: row.employeeCode,
                                        employeeName: row.employeeName,
                                        basicSalary: row.basicSalary,
                                        workingDays: row.workingDays,
                                        presentDays: row.presentDays,
                                        extraDays: row.extraDays || 0,
                                        earnedSalary: row.earnedSalary,
                                        awardAmount: row.awardAmount,
                                        allowance: row.allowance || 0,
                                        arrears: row.arrears || 0,
                                        compOffPay: row.compOffPay || 0,
                                        advanceDeduction: row.advanceDeduction,
                                        otherDeductions: row.otherDeductions,
                                        netPayable: row.netPayable,
                                        paymentMode: row._paymentMode,
                                        paymentDate: row._paymentDate || null,
                                        transactionRef: row._transactionRef,
                                        status: row.status || 'Draft',
                                        remarks: row._remarks,
                                      });
                                    } catch (e) { alert('Save failed'); }
                                  }}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        : fmt2(row.netPayable)
                      }
                    </TableCell>

                    {/* Inline editable payment fields — locked for Approved/Paid */}
                    <TableCell>
                      {isLocked ? (row._paymentMode || '—') : (
                        <Select size="small" variant="standard" value={row._paymentMode}
                          onChange={e => updateRow(idx, '_paymentMode', e.target.value)}
                          sx={{ minWidth: 110 }}>
                          <MenuItem value="">—</MenuItem>
                          {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {isPaid ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField size="small" type="date" variant="standard"
                            value={row._paymentDate || ''}
                            inputProps={{ style: { padding: '2px 0' } }}
                            onChange={e => updateRow(idx, '_paymentDate', e.target.value)} />
                          <Tooltip title="Save payment date">
                            <span>
                              <IconButton size="small" color="primary"
                                onClick={async () => {
                                  try {
                                    await api.patch(`/salary/sheet/${row.id}/payment-date`, {
                                      paymentDate: row._paymentDate,
                                      paymentMode: row._paymentMode,
                                    });
                                  } catch (e) {
                                    alert(e.response?.data?.message || 'Failed to update payment date');
                                  }
                                }}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : isApproved ? (row._paymentDate || '—') : (
                        <TextField size="small" type="date" variant="standard"
                          value={row._paymentDate}
                          inputProps={{ style: { padding: '2px 0' } }}
                          onChange={e => updateRow(idx, '_paymentDate', e.target.value)} />
                      )}
                    </TableCell>
                    <TableCell>
                      {isLocked ? (row._remarks || '—') : (
                        <TextField size="small" variant="standard" placeholder="Remarks"
                          value={row._remarks}
                          inputProps={{ style: { width: 100, padding: '2px 0' } }}
                          onChange={e => updateRow(idx, '_remarks', e.target.value)} />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      {isPaid ? (
                        <Chip label="Paid" size="small" color="success" />
                      ) : (
                        <Box>
                          <Select
                            size="small" variant="standard"
                            value={row.status || 'Draft'}
                            disabled={!row.id}
                            onChange={e => handleStatusSelect(row, e.target.value)}
                            renderValue={(v) => (
                              <Chip label={v} size="small" color={statusColor} sx={{ cursor: 'pointer' }} />
                            )}
                            sx={{ '& .MuiSelect-select': { p: 0 }, '& .MuiSelect-icon': { display: row.id ? 'block' : 'none' } }}
                          >
                            {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                          {isRejected && row.rejectionReason && (
                            <Tooltip title={`Reason: ${row.rejectionReason}`}>
                              <Typography variant="caption" color="error.main" sx={{ display: 'block', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                                {row.rejectionReason}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {!isLocked && !isRejected && (
                        <Tooltip title="Adjust salary figures">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.id && (
                        <Tooltip title="Reset — clear saved data and recalculate fresh">
                          <IconButton size="small" color="warning" onClick={() => handleReset(row.id)}>
                            <ResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.id && !isPaid && (
                        <Tooltip title="Delete this salary entry">
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit salary figures dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Salary — {editRow?.employeeName}</DialogTitle>
        <DialogContent>
          {editRow && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField label="Basic Salary" type="number" size="small" fullWidth
                  value={editRow.basicSalary} onChange={e => setEditRow({ ...editRow, basicSalary: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Present Days" type="number" size="small" fullWidth
                  value={editRow.presentDays} onChange={e => setEditRow({ ...editRow, presentDays: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Earned Salary" type="number" size="small" fullWidth
                  value={editRow.earnedSalary} onChange={e => setEditRow({ ...editRow, earnedSalary: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Award Amount" type="number" size="small" fullWidth
                  value={editRow.awardAmount} onChange={e => setEditRow({ ...editRow, awardAmount: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Allowance" type="number" size="small" fullWidth
                  value={editRow.allowance || 0} onChange={e => setEditRow({ ...editRow, allowance: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Arrears" type="number" size="small" fullWidth
                  value={editRow.arrears || 0} onChange={e => setEditRow({ ...editRow, arrears: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Advance Deduction" type="number" size="small" fullWidth
                  value={editRow.advanceDeduction} onChange={e => setEditRow({ ...editRow, advanceDeduction: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Other Deductions" type="number" size="small" fullWidth
                  value={editRow.otherDeductions} onChange={e => setEditRow({ ...editRow, otherDeductions: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Net Payable" type="number" size="small" fullWidth
                  value={editRow.netPayable} onChange={e => setEditRow({ ...editRow, netPayable: e.target.value })}
                  InputProps={{ sx: { fontWeight: 700 } }} />
              </Grid>
              {editRow.bankInfo && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">Bank Details</Typography>
                    <Typography variant="body2">{editRow.bankInfo.accountHolderName || editRow.employeeName}</Typography>
                    <Typography variant="body2">{editRow.bankInfo.bankName} — {editRow.bankInfo.bankAccountNumber}</Typography>
                    <Typography variant="body2">IFSC: {editRow.bankInfo.bankIfscCode}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Historical Salary dialog */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon color="info" /> Import Historical Salary Data
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Step 1 — Download Template</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select value={importMonth} label="Month" onChange={e => setImportMonth(e.target.value)}>
                {MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Year</InputLabel>
              <Select value={importYear} label="Year" onChange={e => setImportYear(e.target.value)}>
                {[2026, 2025, 2024, 2023].map(y => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} size="small">
              Download Template
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            The template is pre-filled with all active employees. Fill in the salary figures and upload below.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Step 2 — Upload Filled File</Typography>
          <Box
            component="label"
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              border: '2px dashed', borderColor: importFile ? 'success.main' : 'grey.400',
              borderRadius: 2, p: 3, cursor: 'pointer', bgcolor: importFile ? '#f0fdf4' : '#fafafa', mb: 2
            }}
          >
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleImportFileChange} />
            <UploadIcon sx={{ fontSize: 36, color: importFile ? 'success.main' : 'grey.400', mb: 1 }} />
            {importFile
              ? <Typography color="success.main" fontWeight={600}>{importPreview?.name} ({importPreview?.size})</Typography>
              : <Typography color="text.secondary">Click to select file (.xlsx / .xls)</Typography>}
          </Box>
          {importResult && (
            <Alert severity={importResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
              <Typography fontWeight={600}>{importResult.message}</Typography>
              {importResult.success && importResult.data && (
                <Box sx={{ mt: 0.5, fontSize: '0.82rem' }}>
                  <Box>✅ Inserted: {importResult.data.inserted} &nbsp;|&nbsp; ✏️ Updated: {importResult.data.updated} &nbsp;|&nbsp; ⏭️ Skipped: {importResult.data.skipped}</Box>
                  {importResult.data.errors?.length > 0 && (
                    <Box sx={{ mt: 0.5, color: 'warning.dark' }}>
                      {importResult.data.errors.map((e, i) => <div key={i}>⚠️ {e}</div>)}
                    </Box>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>Close</Button>
          <Button
            variant="contained" color="info"
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            {importing ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection reason dialog */}
      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject — {rejectTarget?.employeeName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus label="Rejection Reason" fullWidth multiline rows={3} size="small"
            sx={{ mt: 1 }}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="e.g. On notice period, attendance discrepancy…"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Mark Paid confirmation dialog */}
      {payTarget && (
        <PayConfirmDialog
          row={payTarget}
          onClose={() => setPayTarget(null)}
          onConfirm={confirmPay}
        />
      )}

      {/* Bulk Set Payment Date + Remark dialog */}
      <Dialog open={bulkApplyOpen} onClose={() => setBulkApplyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Payment Date &amp; Remark for All Rows</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applies to all non-Paid rows. Leave Remark blank to keep existing remarks.
          </Typography>
          <TextField
            fullWidth size="small" label="Payment Date" type="date"
            value={bulkDate}
            onChange={e => setBulkDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Remark (optional)"
            value={bulkRemark}
            onChange={e => setBulkRemark(e.target.value)}
            placeholder="e.g. May 2026 Salary"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkApplyOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkApply} disabled={!bulkDate}>
            Apply to All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank File dialog */}
      <Dialog open={bankFileOpen} onClose={() => !bankFileLoading && setBankFileOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate Bank Files</DialogTitle>
        <DialogContent>
          {bankFileIncomplete.length > 0 ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Cannot generate files.</strong> The following {bankFileIncomplete.length} employee(s) have incomplete bank details. Please update their profiles and try again.
              </Alert>
              {bankFileIncomplete.map((e, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2" fontWeight={500}>{e.employeeName} <Typography component="span" variant="caption" color="text.secondary">({e.employeeCode})</Typography></Typography>
                  <Typography variant="caption" color="error.main">Missing: {e.missing.join(', ')}</Typography>
                </Box>
              ))}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the company's AXIS debit account number. Two files will be downloaded:
                <br />• <strong>AXIS file</strong> — employees with AXIS bank accounts
                <br />• <strong>All Banks file</strong> — all other bank accounts
              </Typography>
              <TextField
                fullWidth size="small"
                label="Company Debit Account Number"
                value={debitAccount}
                onChange={e => setDebitAccount(e.target.value)}
                placeholder="e.g. 922020045269368"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {bankFileIncomplete.length > 0 ? (
            <Button variant="contained" onClick={() => { setBankFileOpen(false); setBankFileIncomplete([]); }}>Close</Button>
          ) : (
            <>
              <Button onClick={() => setBankFileOpen(false)} disabled={bankFileLoading}>Cancel</Button>
              <Button variant="contained" onClick={handleBankFileDownload} disabled={bankFileLoading || !debitAccount.trim()}>
                {bankFileLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Download Files
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Recovery Review Dialog (Phase 2) */}
      <Dialog open={recoveryOpen} onClose={() => !applyingRecovery && setRecoveryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Salary Recoveries</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            The following employees had projected days last month that didn't match actual attendance.
            Select which recoveries to apply to <strong>{month}-{year}</strong> salary as a prepaid deduction.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell padding="checkbox"><input type="checkbox"
                  checked={recoveryPending.length > 0 && recoveryPending.every(r => recoverySelections[r.projectionId])}
                  onChange={e => {
                    const sel = {};
                    recoveryPending.forEach(r => { sel[r.projectionId] = e.target.checked; });
                    setRecoverySelections(sel);
                  }} /></TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Projected Days</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Day Details</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Recovery (₹)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recoveryPending.map(r => (
                <TableRow key={r.projectionId} hover>
                  <TableCell padding="checkbox">
                    <input type="checkbox"
                      checked={!!recoverySelections[r.projectionId]}
                      onChange={e => setRecoverySelections(prev => ({ ...prev, [r.projectionId]: e.target.checked }))} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.employeeName}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.employeeCode}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">Days: {r.projectedDaysList.join(', ')}</Typography>
                    <Typography variant="caption" color="text.secondary">Projected: ₹{fmt2(r.projectedAmount)}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.details.map((d, i) => (
                      <Box key={i} component="span" sx={{ mr: 1, fontSize: '0.75rem' }}>
                        Day {d.day}: <Chip label={d.status} size="small" color={d.recovery > 0 ? 'error' : 'success'} sx={{ height: 18, fontSize: '0.65rem' }} />
                        {d.recovery > 0 && ` -₹${fmt2(d.recovery)}`}
                      </Box>
                    ))}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>₹{fmt2(r.recoveryAmount)}</TableCell>
                  <TableCell>
                    <Button size="small" color="warning" onClick={() => handleWaiveRecovery(r.projectionId)}>Waive</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecoveryOpen(false)} disabled={applyingRecovery}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleApplyRecovery}
            disabled={applyingRecovery || !Object.values(recoverySelections).some(Boolean)}>
            {applyingRecovery ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Apply Selected Recoveries
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Salary Advances ─────────────────────────────────────────────────────────

function SalaryAdvances() {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('Active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);

  const loadAdvances = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/salary/advances', { params });
      setAdvances(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load advances');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  useEffect(() => {
    api.get('/users?isActive=true').then(res => {
      const users = (res.data.data?.users || res.data.data || res.data || []).filter(u => u.employeeId);
      setEmployees(users);
    }).catch(() => {});
  }, []);

  const openAdd = () => {
    setForm({
      advanceDate: new Date().toISOString().split('T')[0],
      totalAmount: '',
      recoveryMonths: 1,
      reason: '',
      remarks: ''
    });
    setDialogMode('add');
    setDialogOpen(true);
  };

  const openEdit = (adv) => {
    setForm({ ...adv });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleEmployeeSelect = (userId) => {
    const u = employees.find(e => e.id === userId);
    if (u) setForm(f => ({ ...f, userId: u.id, employeeCode: u.employeeId, employeeName: u.fullName }));
  };

  const handleSave = async () => {
    if (dialogMode === 'add') {
      if (!form.userId) return alert('Please select an employee');
      if (!form.totalAmount || parseFloat(form.totalAmount) <= 0) return alert('Please enter Total Amount');
    }
    setSaving(true);
    try {
      if (dialogMode === 'add') {
        await api.post('/salary/advances', form);
      } else {
        await api.put(`/salary/advances/${form.id}`, form);
      }
      setDialogOpen(false);
      loadAdvances();
    } catch (e) {
      alert(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this advance record?')) return;
    try {
      await api.delete(`/salary/advances/${id}`);
      loadAdvances();
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <Box>
      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Fully Recovered">Fully Recovered</MenuItem>
              <MenuItem value="Waived">Waived</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={loadAdvances} startIcon={<RefreshIcon />}>Refresh</Button>
          <Button variant="contained" onClick={openAdd} startIcon={<AddIcon />} sx={{ ml: 'auto' }}>
            Add Advance
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
                <TableCell>Employee</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Advance Date</TableCell>
                <TableCell align="right">Total (₹)</TableCell>
                <TableCell align="right">Monthly Ded. (₹)</TableCell>
                <TableCell>Recovery (months)</TableCell>
                <TableCell>Recovery Progress</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Given By</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {advances.length === 0 && (
                <TableRow><TableCell colSpan={11} align="center">No records found</TableCell></TableRow>
              )}
              {advances.map(adv => {
                const recovered = parseFloat(adv.recoveredAmount || 0);
                const total = parseFloat(adv.totalAmount || 1);
                const pct = Math.min(100, Math.round((recovered / total) * 100));
                return (
                  <TableRow key={adv.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{adv.employeeName}</TableCell>
                    <TableCell>{adv.employeeCode}</TableCell>
                    <TableCell>{adv.advanceDate}</TableCell>
                    <TableCell align="right">{fmt2(adv.totalAmount)}</TableCell>
                    <TableCell align="right">{fmt2(adv.monthlyDeduction)}</TableCell>
                    <TableCell align="center">{adv.recoveryMonths}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                          ₹{fmt2(recovered)} / ₹{fmt2(total)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={adv.status}
                        size="small"
                        color={adv.status === 'Active' ? 'warning' : adv.status === 'Fully Recovered' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={adv.reason || ''}><span>{adv.reason || '—'}</span></Tooltip>
                    </TableCell>
                    <TableCell>{adv.givenBy || '—'}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(adv)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(adv.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === 'add' ? 'Add Salary Advance' : 'Edit Salary Advance'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {dialogMode === 'add' && (
              <Grid item xs={12}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Employee</InputLabel>
                  <Select value={form.userId || ''} label="Employee"
                    onChange={e => handleEmployeeSelect(e.target.value)}>
                    {employees.map(u => (
                      <MenuItem key={u.id} value={u.id}>{u.fullName} ({u.employeeId})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {dialogMode === 'edit' && (
              <Grid item xs={12}>
                <TextField label="Employee" size="small" fullWidth disabled value={form.employeeName || ''} />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField label="Advance Date" type="date" size="small" fullWidth
                value={form.advanceDate || ''} InputLabelProps={{ shrink: true }}
                onChange={e => setForm(f => ({ ...f, advanceDate: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Total Amount (₹)" type="number" size="small" fullWidth required
                value={form.totalAmount || ''} onChange={e => setForm(f => ({ ...f, totalAmount: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Recovery Months" type="number" size="small" fullWidth
                value={form.recoveryMonths || 1} inputProps={{ min: 1 }}
                onChange={e => setForm(f => ({ ...f, recoveryMonths: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Recovered Amount (₹)" type="number" size="small" fullWidth
                value={form.recoveredAmount || 0}
                onChange={e => setForm(f => ({ ...f, recoveredAmount: e.target.value }))} />
            </Grid>
            {dialogMode === 'edit' && (
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={form.status || 'Active'} label="Status"
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Fully Recovered">Fully Recovered</MenuItem>
                    <MenuItem value="Waived">Waived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField label="Reason" size="small" fullWidth multiline rows={2}
                value={form.reason || ''} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Remarks" size="small" fullWidth
                value={form.remarks || ''} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Salary Prepaids ─────────────────────────────────────────────────────────

function SalaryPrepaids() {
  const [prepaids, setPrepaids]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth, setFilterMonth]   = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [employees, setEmployees]   = useState([]);
  const [presentDays, setPresentDays]       = useState(null);
  const [presentDaysLoading, setPresentDaysLoading] = useState(false);

  const loadPrepaids = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterStatus) params.status    = filterStatus;
      if (filterMonth)  params.monthYear = filterMonth;
      const res = await api.get('/salary/prepaids', { params });
      setPrepaids(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load prepaids');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterMonth]);

  useEffect(() => { loadPrepaids(); }, [loadPrepaids]);

  useEffect(() => {
    api.get('/users?isActive=true').then(res => {
      const users = (res.data.data?.users || res.data.data || res.data || []).filter(u => u.employeeId);
      setEmployees(users);
    }).catch(() => {});
  }, []);

  // Fetch present days for the selected employee+month to validate prepaid days
  useEffect(() => {
    if (!dialogOpen) return;
    const empCode = form.employeeCode;
    const my = form.monthYear;
    if (!empCode || !my || !my.includes('-')) { setPresentDays(null); return; }
    setPresentDaysLoading(true);
    api.get(`/salary/present-days/${encodeURIComponent(my)}/${encodeURIComponent(empCode)}`)
      .then(res => setPresentDays(res.data.data.daysPresent))
      .catch(() => setPresentDays(null))
      .finally(() => setPresentDaysLoading(false));
  }, [dialogOpen, form.employeeCode, form.monthYear]);

  const handleEmployeeSelect = (userId) => {
    const u = employees.find(e => e.id === userId);
    if (!u) return;
    const dailyRate = u.salary ? Math.round((parseFloat(u.salary) / 26) * 100) / 100 : '';
    const days = parseFloat(form.daysWorked) || 0;
    setForm(f => ({
      ...f,
      userId: u.id,
      employeeCode: u.employeeId,
      employeeName: u.fullName,
      dailyRate,
      prepaidAmount: days > 0 && dailyRate ? Math.round(days * dailyRate * 100) / 100 : f.prepaidAmount
    }));
  };

  const handleDaysChange = (val) => {
    const days = parseFloat(val) || 0;
    const rate = parseFloat(form.dailyRate) || 0;
    setForm(f => ({
      ...f,
      daysWorked: val,
      prepaidAmount: days > 0 && rate > 0 ? Math.round(days * rate * 100) / 100 : f.prepaidAmount
    }));
  };

  const handleRateChange = (val) => {
    const rate = parseFloat(val) || 0;
    const days = parseFloat(form.daysWorked) || 0;
    setForm(f => ({
      ...f,
      dailyRate: val,
      prepaidAmount: days > 0 && rate > 0 ? Math.round(days * rate * 100) / 100 : f.prepaidAmount
    }));
  };

  const openAdd = () => {
    setForm({ paymentDate: new Date().toISOString().split('T')[0], daysWorked: '', dailyRate: '', prepaidAmount: '', remarks: '' });
    setDialogMode('add');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setForm({ ...p });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (dialogMode === 'add') {
      if (!form.userId)                                    return alert('Please select an employee');
      if (!form.monthYear)                                 return alert('Please enter month-year (e.g. May-2026)');
      if (!form.daysWorked || parseFloat(form.daysWorked) <= 0) return alert('Please enter days worked');
      if (!form.prepaidAmount || parseFloat(form.prepaidAmount) <= 0) return alert('Prepaid amount is required');
      if (!form.paymentDate)                               return alert('Please enter payment date');
    }
    setSaving(true);
    try {
      if (dialogMode === 'add') {
        await api.post('/salary/prepaids', form);
      } else {
        await api.put(`/salary/prepaids/${form.id}`, form);
      }
      setDialogOpen(false);
      loadPrepaids();
    } catch (e) {
      alert(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prepaid record?')) return;
    try {
      await api.delete(`/salary/prepaids/${id}`);
      loadPrepaids();
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    }
  };

  const isLocked = form.status === 'Adjusted';
  const daysOverLimit = presentDays !== null && parseFloat(form.daysWorked) > presentDays;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Adjusted">Adjusted</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small" label="Month (e.g. May-2026)"
            value={filterMonth}
            onChange={e => setFilterMonth(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <Button variant="outlined" onClick={loadPrepaids} startIcon={<RefreshIcon />}>Refresh</Button>
          <Button variant="contained" onClick={openAdd} startIcon={<AddIcon />} sx={{ ml: 'auto' }}>
            Add Prepaid
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
                <TableCell>Employee</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Month</TableCell>
                <TableCell align="right">Days</TableCell>
                <TableCell align="right">Daily Rate</TableCell>
                <TableCell align="right">Prepaid Amt</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Given By</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prepaids.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No prepaid records found
                  </TableCell>
                </TableRow>
              )}
              {prepaids.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.employeeName}</TableCell>
                  <TableCell>{p.employeeCode}</TableCell>
                  <TableCell>{p.monthYear}</TableCell>
                  <TableCell align="right">{p.daysWorked}</TableCell>
                  <TableCell align="right">₹{fmt2(p.dailyRate)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>₹{fmt2(p.prepaidAmount)}</TableCell>
                  <TableCell>{p.paymentDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.status}
                      size="small"
                      color={p.status === 'Adjusted' ? 'success' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>{p.givenBy || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={p.remarks || ''}><span>{p.remarks || '—'}</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {p.status === 'Paid' && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === 'add' ? 'Add Salary Prepaid' : 'Edit Salary Prepaid'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {dialogMode === 'add' ? (
              <Grid item xs={12}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Employee</InputLabel>
                  <Select value={form.userId || ''} label="Employee" onChange={e => handleEmployeeSelect(e.target.value)}>
                    {employees.map(u => (
                      <MenuItem key={u.id} value={u.id}>{u.fullName} ({u.employeeId})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
                  {form.employeeName} ({form.employeeCode})
                  {isLocked && <Chip label="Adjusted — read only" size="small" color="success" sx={{ ml: 1 }} />}
                </Typography>
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                label="Month-Year (e.g. May-2026)" size="small" fullWidth
                value={form.monthYear || ''}
                onChange={e => setForm(f => ({ ...f, monthYear: e.target.value }))}
                disabled={isLocked}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Payment Date" size="small" fullWidth type="date"
                InputLabelProps={{ shrink: true }}
                value={form.paymentDate || ''}
                onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))}
                disabled={isLocked}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Days Worked" size="small" fullWidth type="number"
                inputProps={{ step: 0.5, min: 0 }}
                value={form.daysWorked || ''}
                onChange={e => handleDaysChange(e.target.value)}
                disabled={isLocked}
                error={daysOverLimit}
                helperText={
                  presentDaysLoading ? 'Checking attendance…' :
                  presentDays !== null ? `Max: ${presentDays} days present this month` :
                  ''
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Daily Rate (₹)" size="small" fullWidth type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={form.dailyRate || ''}
                onChange={e => handleRateChange(e.target.value)}
                helperText="salary ÷ 26"
                disabled={isLocked}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Prepaid Amount (₹)" size="small" fullWidth type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={form.prepaidAmount || ''}
                onChange={e => setForm(f => ({ ...f, prepaidAmount: e.target.value }))}
                disabled={isLocked}
              />
            </Grid>
            {daysOverLimit && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ py: 0.5 }}>
                  Days worked ({form.daysWorked}) exceeds present days ({presentDays}) for this month. Reduce days worked to avoid a negative salary.
                </Alert>
              </Grid>
            )}
            {dialogMode === 'edit' && (
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={form.status || 'Paid'} label="Status"
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    disabled={isLocked}
                  >
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Adjusted">Adjusted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Remarks" size="small" fullWidth multiline rows={2}
                value={form.remarks || ''}
                onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || isLocked || daysOverLimit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

// ─── Payroll Insights ────────────────────────────────────────────────────────

const INR = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const INR2 = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PIE_COLORS = ['#1976d2', '#43a047', '#9c27b0', '#ef6c00', '#e53935'];

const shortMonth = (my) => {
  const [m] = my.split('-');
  return m.slice(0, 3);
};

function SummaryCard({ label, value, color, icon }) {
  return (
    <Card elevation={2} sx={{ flex: 1, minWidth: 160 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

function EmployeeRow({ emp }) {
  const [open, setOpen] = useState(false);
  const pieData = [
    { name: 'Earned', value: emp.totals.earned },
    { name: 'Awards', value: emp.totals.awards },
    { name: 'Comp-Off', value: emp.totals.compOffPay },
  ].filter(d => d.value > 0);
  const totalGross = emp.totals.earned + emp.totals.awards + emp.totals.compOffPay;

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <TableCell sx={{ fontWeight: 600 }}>{emp.employeeName}</TableCell>
        <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{emp.employeeCode}</TableCell>
        <TableCell align="center">{emp.totals.months}</TableCell>
        <TableCell align="right">{INR(emp.totals.earned)}</TableCell>
        <TableCell align="right" sx={{ color: 'success.main' }}>{emp.totals.awards > 0 ? INR(emp.totals.awards) : '—'}</TableCell>
        <TableCell align="right" sx={{ color: '#9c27b0' }}>{emp.totals.compOffPay > 0 ? INR(emp.totals.compOffPay) : '—'}</TableCell>
        <TableCell align="right" sx={{ color: 'error.main' }}>{emp.totals.deductions > 0 ? INR(emp.totals.deductions) : '—'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>{INR(emp.totals.net)}</TableCell>
        <TableCell align="center">
          <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#fafafa', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* Monthly detail table */}
              <Box sx={{ flex: 2, minWidth: 320 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>MONTHLY BREAKDOWN</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.7rem', bgcolor: '#f0f4f8' } }}>
                      <TableCell>Month</TableCell>
                      <TableCell align="center">Days</TableCell>
                      <TableCell align="right">Earned</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>Award</TableCell>
                      <TableCell align="right" sx={{ color: '#9c27b0' }}>CO Pay</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>Ded.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emp.months.map(m => (
                      <TableRow key={m.monthYear} hover>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{m.monthYear}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{m.presentDays}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{INR2(m.earnedSalary)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'success.main' }}>{m.awardAmount > 0 ? INR2(m.awardAmount) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#9c27b0' }}>{m.compOffPay > 0 ? INR2(m.compOffPay) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'error.main' }}>
                          {(m.advanceDeduction + m.otherDeductions) > 0 ? INR2(m.advanceDeduction + m.otherDeductions) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{INR2(m.netPayable)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {/* Pie chart */}
              {pieData.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>GROSS COMPOSITION</Typography>
                  <PieChart width={180} height={150}>
                    <Pie data={pieData} cx={90} cy={70} innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <ReTooltip formatter={(v) => INR2(v)} />
                  </PieChart>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                    {pieData.map((d, i) => (
                      <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <Typography variant="caption">{d.name}: {INR(d.value)} ({totalGross > 0 ? Math.round(d.value / totalGross * 100) : 0}%)</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function PayrollInsights() {
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2].map(String);

  const [view, setView] = useState('monthly');
  const [year, setYear] = useState(String(currentYear));
  const [rollingMonths] = useState(12);
  const [empFilter, setEmpFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartType, setChartType] = useState('bar');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ view });
      if (view === 'monthly') params.set('year', year);
      if (view === 'rolling') params.set('months', rollingMonths);
      if (empFilter) params.set('employeeCode', empFilter);
      const res = await api.get(`/salary/analytics?${params}`);
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [view, year, rollingMonths, empFilter]);

  useEffect(() => { load(); }, [load]);

  const employees = data?.employees || [];
  const trend = data?.trend || [];
  const summary = data?.summary || {};
  const empNames = employees.map(e => ({ code: e.employeeCode, name: e.employeeName }));

  const trendChartData = trend.map(t => ({
    name: shortMonth(t.monthYear) + (view !== 'monthly' ? ` '${t.monthYear.split('-')[1].slice(2)}` : ''),
    'Net Payable': t.totalNet,
    'Earned': t.totalEarned,
    'Awards': t.totalAwards,
    'Comp-Off': t.totalCompOff,
    'Deductions': t.totalDeductions,
  }));

  return (
    <Box>
      {/* ── Filter bar ── */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="monthly">By Year</ToggleButton>
          <ToggleButton value="rolling">Last 12 Months</ToggleButton>
          <ToggleButton value="yearly">All Time</ToggleButton>
        </ToggleButtonGroup>

        {view === 'monthly' && (
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
              {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Employee</InputLabel>
          <Select value={empFilter} label="Employee" onChange={e => setEmpFilter(e.target.value)}>
            <MenuItem value="">All Employees</MenuItem>
            {empNames.map(e => <MenuItem key={e.code} value={e.code}>{e.name}</MenuItem>)}
          </Select>
        </FormControl>

        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        <Box sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.78rem' }}>
          {summary.monthsCovered > 0 && `${summary.monthsCovered} months · ${summary.employeeCount} employees`}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {data && (
        <>
          {/* ── Summary cards ── */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <SummaryCard label="Total Payroll" value={INR(summary.totalPayroll)} color="#1976d2" icon={<SalaryIcon />} />
            <SummaryCard label="Total Earned" value={INR(summary.totalEarned)} color="#0288d1" icon={<TrendingUpIcon />} />
            <SummaryCard label="Total Awards" value={INR(summary.totalAwards)} color="#43a047" icon={<AwardIcon />} />
            <SummaryCard label="Comp-Off Pay" value={INR(summary.totalCompOffPay)} color="#9c27b0" icon={<CompOffIcon />} />
            <SummaryCard label="Total Deductions" value={INR(summary.totalDeductions)} color="#e53935" icon={<PeopleIcon />} />
            <SummaryCard label="Avg Monthly Net" value={INR(summary.avgMonthlyNet)} color="#f57c00" icon={<TrendingUpIcon />} />
          </Box>

          {/* ── Trend chart ── */}
          {trendChartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>Payroll Trend</Typography>
                <ToggleButtonGroup value={chartType} exclusive size="small" onChange={(_, v) => v && setChartType(v)}>
                  <ToggleButton value="bar">Stacked</ToggleButton>
                  <ToggleButton value="line">Lines</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <ResponsiveContainer width="100%" height={280}>
                {chartType === 'bar' ? (
                  <BarChart data={trendChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => '₹' + (v >= 100000 ? (v / 100000).toFixed(1) + 'L' : (v / 1000).toFixed(0) + 'k')} tick={{ fontSize: 11 }} />
                    <ReTooltip formatter={(v, n) => [INR2(v), n]} />
                    <Legend />
                    <Bar dataKey="Earned" stackId="a" fill="#1976d2" />
                    <Bar dataKey="Awards" stackId="a" fill="#43a047" />
                    <Bar dataKey="Comp-Off" stackId="a" fill="#9c27b0" />
                    <Bar dataKey="Deductions" stackId="a" fill="#ef9a9a" />
                  </BarChart>
                ) : (
                  <LineChart data={trendChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => '₹' + (v >= 100000 ? (v / 100000).toFixed(1) + 'L' : (v / 1000).toFixed(0) + 'k')} tick={{ fontSize: 11 }} />
                    <ReTooltip formatter={(v, n) => [INR2(v), n]} />
                    <Legend />
                    <Line type="monotone" dataKey="Net Payable" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Earned" stroke="#42a5f5" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
                    <Line type="monotone" dataKey="Awards" stroke="#43a047" strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="Comp-Off" stroke="#9c27b0" strokeWidth={1.5} dot={false} />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </Paper>
          )}

          {/* ── Employee breakdown table ── */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              Employee Breakdown
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Click a row to expand monthly details &amp; composition chart
              </Typography>
            </Typography>
            {employees.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No paid salary records found for this period.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5', fontSize: '0.78rem' } }}>
                      <TableCell>Employee</TableCell>
                      <TableCell align="center">Code</TableCell>
                      <TableCell align="center">Months</TableCell>
                      <TableCell align="right">Earned (₹)</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>Awards (₹)</TableCell>
                      <TableCell align="right" sx={{ color: '#9c27b0' }}>CO Pay (₹)</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>Deductions (₹)</TableCell>
                      <TableCell align="right">Net Payable (₹)</TableCell>
                      <TableCell align="center" sx={{ width: 40 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.map(emp => <EmployeeRow key={emp.employeeCode} emp={emp} />)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}


export default function EmployeeSalary() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SalaryIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>Employee Salary</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Salary Sheet" />
        <Tab label="Salary Advances" />
        <Tab label="Salary Prepaid" />
        <Tab label="Payroll Insights" />
      </Tabs>

      {tab === 0 && <SalarySheet />}
      {tab === 1 && <SalaryAdvances />}
      {tab === 2 && <SalaryPrepaids />}
      {tab === 3 && <PayrollInsights />}
    </Box>
  );
}
